import { combineReducers } from 'redux';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { CashPaymentInfoRequest, PaymentDocument, PostSetPaymentRequest } from 'generated/bffInvoices/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  AppAction,
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createLoadingStateReducer,
  createReducer,
  LoadingStatus,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { performApiCall } from 'saga/apiClient/ducks';
import { getKahlActions } from 'saga/kahl/ducks';

/* STATE */
export interface InvoicesToPayState {
  data: PaymentDocument[];
  loadingStatus: LoadingStatus;
  cashPaymentUrl: string;
}

/* ACTION TYPES */
export enum InvoicesToPayTypes {
  GetPaymentInfo = '@@Billing/InvoicesToPay/GET_PAYMENT_INFO',
  RemovePaymentItem = '@@Billing/InvoicesToPay/REMOVE_PAYMENT_ITEM',
  PostCreateCashPayment = '@@Billing/InvoicesToPay/POST_CREATE_CASH_PAYMENT',
  SetLoading = '@@Billing/InvoicesToPay/SET_LOADING',
}

/* REDUCERS */
const initialState: InvoicesToPayState = {
  data: [],
  loadingStatus: LoadingStatus.loading,
  cashPaymentUrl: '',
};

const data = createReducer(initialState.data, {
  [InvoicesToPayTypes.GetPaymentInfo]: {
    [RequestActionTypes.SUCCESS]: (_state: PaymentDocument[], payload: PaymentDocument) => payload,
    [RequestActionTypes.FAILURE]: () => {
      return [];
    },
  },
  [InvoicesToPayTypes.RemovePaymentItem]: (_state: PaymentDocument[], document: PaymentDocument) => {
    return _state.filter((item) => item !== document);
  },
});

const loadingStatus = createLoadingStateReducer(
  initialState.loadingStatus,
  {
    [InvoicesToPayTypes.GetPaymentInfo]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
  },
  {
    [InvoicesToPayTypes.SetLoading]: (state: LoadingStatus, status: LoadingStatus) => status,
  }
);

const cashPaymentUrl = createReducer(initialState.cashPaymentUrl, {
  [InvoicesToPayTypes.PostCreateCashPayment]: {
    [RequestActionTypes.SUCCESS]: (_state: PostSetPaymentRequest, payload: string) => payload,
    [RequestActionTypes.FAILURE]: () => '',
  },
});

export default combineReducers<InvoicesToPayState>({
  data,
  loadingStatus,
  cashPaymentUrl,
});

/* SELECTORS */
export const selectInvoicesToPayState = (state: AppState) => state.invoicesToPay;
export const selectGetPaymentInfoData = (state: AppState): PaymentDocument[] => selectInvoicesToPayState(state).data;
export const selectPaymentInfoDataLoading = (state: AppState): LoadingStatus =>
  selectInvoicesToPayState(state).loadingStatus;

const selectCashPaymentUrl = (state: AppState) => selectInvoicesToPayState(state).cashPaymentUrl;

/* ACTIONS */
export const getPaymentInfoAction = createApiActionCreators(InvoicesToPayTypes.GetPaymentInfo);
export const removePaymentItemAction = createActionCreator(InvoicesToPayTypes.RemovePaymentItem);
export const setLoadingAction = createActionCreator(InvoicesToPayTypes.SetLoading);
export const postCreateCashPaymentActions = createApiActionCreators(InvoicesToPayTypes.PostCreateCashPayment);

/* SAGAS */
export function* getPaymentInfo({ payload }: AppAction<CashPaymentInfoRequest>) {
  yield put(setLoadingAction(LoadingStatus.loading));

  const request: ApiClientRequest = {
    apiCall: call(api.getPaymentInfo, environment.properties['bff.invoices.url'], payload),
  };

  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getPaymentInfoAction.success(resp.data.paymentDocuments));
  } else {
    yield put(getPaymentInfoAction.failure());
  }
}

function* postCreateCashPayment({ payload }: AppAction<PostSetPaymentRequest>) {
  const request: ApiClientRequest = {
    apiCall: call(api.postSetPayment, environment.properties['bff.invoices.url'], payload),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok && resp.data && resp.data.cashPaymentUrl) {
    yield put(postCreateCashPaymentActions.success(resp.data.cashPaymentUrl));
    yield put(setLoadingAction(LoadingStatus.success));
  } else {
    yield put(postCreateCashPaymentActions.failure());
    yield put(setLoadingAction(LoadingStatus.failure));
    yield put(getKahlActions.request({ infoMessageIds: 'ID0013' }));
  }
}

function* redirectToCashPaymentUrl() {
  const url: string = yield select(selectCashPaymentUrl);

  if (url) {
    window.location.href = url;
  }
}

/* EXPORT */
export function* invoiceToPaySaga() {
  yield takeLatest(createActionType(InvoicesToPayTypes.GetPaymentInfo, RequestActionTypes.REQUEST), getPaymentInfo);
  yield takeLatest(
    createActionType(InvoicesToPayTypes.PostCreateCashPayment, RequestActionTypes.REQUEST),
    postCreateCashPayment
  );
  yield takeLatest(
    createActionType(InvoicesToPayTypes.PostCreateCashPayment, RequestActionTypes.SUCCESS),
    redirectToCashPaymentUrl
  );
}
